import React, { Fragment, useEffect, useState } from "react";
import { useParams, Navigate, Link } from "react-router-dom";
import Header from "../../components/Header/Header";
import PageTitle from "../../components/pagetitle/PageTitle";
import BlogSidebar from "../../components/BlogSidebar/BlogSidebar";
import ShareButtons from "../../components/ShareButtons/ShareButtons";
import Footer from "../../components/footer/Footer";
import Scrollbar from "../../components/scrollbar/scrollbar";
import SEO from "../../components/SEO/SEO";
import NewsletterSignup from "../../components/NewsletterSignup/NewsletterSignup";
import BlogComments from "../../components/BlogComments/BlogComments";
import RelatedPosts from "../../components/RelatedPosts/RelatedPosts";
import { track } from "../../services/analytics";
import { getMagnetForPost } from "../../api/leadMagnets";

const API_BASE = process.env.REACT_APP_API_BASE_URL || "https://api.axoix.in";

interface ApiPost {
  title: string;
  slug: string;
  excerpt: string | null;
  content: string | null;
  content_html: string | null;
  image: string | null;
  featured_image_alt: string | null;
  author: { id: number; name: string };
  category: { id: number; name: string; slug: string; color: string } | null;
  tags: string[] | null;
  meta_title: string | null;
  meta_description: string | null;
  reading_time_mins: number | null;
  published_at: string | null;
  created_at: string | null;
  allow_comments: boolean;
  comment_count: number;
}

const getImageUrl = (image: string | null): string | null => {
  if (!image) return null;
  if (image.startsWith("http")) return image;
  // Server-relative paths from the mothership (e.g. /uploads/blog/9/featured-...webp)
  // are served by nginx aliases on axoix.com + api.axoix.in. Resolve same-origin
  // when running on axoix.com; otherwise prefix with api.axoix.in.
  if (image.startsWith("/")) {
    if (typeof window !== "undefined" && window.location.host.endsWith("axoix.com")) {
      return image; // same-origin — nginx alias serves it
    }
    return `${API_BASE}${image}`;
  }
  // Bare filename → legacy GPU-generated image route (kept for backward compat)
  return `${API_BASE}/api/chorus/blog/ai/image/${image}`;
};

const formatDate = (isoDate: string | null): string => {
  if (!isoDate) return "";
  const d = new Date(isoDate);
  return d.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
};

const articleStyles = `
  .blog-article-content h3, .blog-article-content h2 {
    margin-top: 35px;
    margin-bottom: 15px;
    font-size: 1.4rem;
  }
  .blog-article-content p {
    line-height: 1.8;
    margin-bottom: 20px;
    opacity: 0.85;
  }
  .blog-article-content ul, .blog-article-content ol {
    margin-bottom: 20px;
    padding-left: 20px;
  }
  .blog-article-content ul li, .blog-article-content ol li {
    margin-bottom: 8px;
    line-height: 1.7;
    opacity: 0.85;
  }
  .blog-article-content a {
    color: var(--color-primary);
    text-decoration: none;
    border-bottom: 1px solid rgba(0,255,151,0.3);
  }
  .blog-article-content a:hover {
    border-bottom-color: var(--color-primary);
  }
  .blog-article-content table {
    width: 100%;
    border-collapse: collapse;
    margin-bottom: 30px;
  }
  .blog-article-content code {
    background: rgba(0,255,151,0.1);
    padding: 2px 6px;
    border-radius: 4px;
    font-size: 0.9em;
  }
  .blog-article-content blockquote {
    border-left: 3px solid var(--color-primary);
    padding: 15px 20px;
    margin: 25px 0;
    background: rgba(0,255,151,0.05);
    border-radius: 0 8px 8px 0;
  }
  .blog-article-content img {
    max-width: 100%;
    height: auto;
    border-radius: 12px;
    margin: 20px 0;
  }
  /* Inline image shortcodes — [image:N align=...] */
  .blog-article-content .blog-inline-image {
    margin: 32px 0;
    text-align: center;
  }
  .blog-article-content .blog-inline-image img {
    margin: 0 auto;
    display: block;
    border-radius: 12px;
    box-shadow: 0 8px 24px rgba(0,0,0,0.18);
  }
  .blog-article-content .blog-inline-image figcaption {
    margin-top: 12px;
    font-size: 14px;
    color: #6b7280;
    font-style: italic;
    text-align: center;
  }
  .blog-article-content .blog-inline-image--left {
    float: left; margin: 8px 24px 16px 0; max-width: 50%;
  }
  .blog-article-content .blog-inline-image--right {
    float: right; margin: 8px 0 16px 24px; max-width: 50%;
  }
  .blog-article-content .blog-inline-image--full img {
    width: 100%;
  }
  .blog-article-content .blog-inline-image--full {
    margin-left: calc(50% - 50vw); margin-right: calc(50% - 50vw);
    max-width: 100vw;
  }
  @media (max-width: 768px) {
    .blog-article-content .blog-inline-image--left,
    .blog-article-content .blog-inline-image--right {
      float: none; max-width: 100%; margin: 24px 0;
    }
  }
  .blog-article-meta {
    display: flex;
    gap: 20px;
    flex-wrap: wrap;
    font-size: 14px;
    opacity: 0.6;
    margin-bottom: 30px;
    padding-bottom: 20px;
    border-bottom: 1px solid rgba(255,255,255,0.1);
  }
  .blog-article-tag {
    display: inline-block;
    background: rgba(0,255,151,0.1);
    color: var(--color-primary);
    font-size: 12px;
    font-weight: 600;
    padding: 4px 12px;
    border-radius: 20px;
    margin-bottom: 15px;
  }
  .blog-article-nav {
    display: flex;
    justify-content: space-between;
    padding: 30px 0;
    border-top: 1px solid rgba(255,255,255,0.1);
    margin-top: 40px;
  }
  .blog-article-nav a {
    color: var(--color-primary);
    text-decoration: none;
    font-size: 14px;
  }
  .blog-featured-image {
    width: 100%;
    height: auto;
    max-height: 640px;
    object-fit: contain;
    display: block;
    margin: 0 auto 30px;
    border-radius: 16px;
    border: 1px solid rgba(0,255,151,0.15);
    background: rgba(0, 2, 15, 0.4);
  }
`;

const BlogArticlePage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const [apiPost, setApiPost] = useState<ApiPost | null>(null);
  const [apiLoading, setApiLoading] = useState(true);

  useEffect(() => {
    if (!slug) return;
    setApiLoading(true);
    fetch(`${API_BASE}/api/chorus/blog/posts/by-slug/${slug}?ref=${encodeURIComponent(document.referrer || '')}`)
      .then((res) => {
        if (!res.ok) throw new Error("Not found");
        return res.json();
      })
      .then((data) => setApiPost(data))
      .catch(() => setApiPost(null))
      .finally(() => setApiLoading(false));
  }, [slug]);

  // Title/meta are handled by the <SEO> component (react-helmet-async) on render.

  // Wait for API check before deciding 404
  if (apiLoading) {
    return (
      <Fragment>
        <div className="about-page inner-page">
          <div className="body_wrap o-clip">
            <Header />
            <main>
              <PageTitle pageTitle="Blog" pagesub="Blog" />
              <section className="pt-120 pb-120">
                <div className="container text-center">
                  <i className="fas fa-spinner fa-spin me-2"></i>Loading article...
                </div>
              </section>
            </main>
            <Footer />
          </div>
        </div>
      </Fragment>
    );
  }

  // 404 if API has no post for this slug
  if (!apiPost) {
    return <Navigate to="/404" replace />;
  }

  const imgUrl = getImageUrl(apiPost.image);
  const tag = apiPost.category?.name || (apiPost.tags && apiPost.tags[0]) || "AXOIX";
  const publishedISO = apiPost.published_at || apiPost.created_at || undefined;
  const magnet = getMagnetForPost(apiPost.category?.name, apiPost.tags);
  const metaDescription =
    apiPost.meta_description || apiPost.excerpt || `${apiPost.title} — from the AXOIX blog.`;

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: apiPost.meta_title || apiPost.title,
    description: metaDescription,
    image: imgUrl || "https://axoix.com/og-image.png",
    datePublished: publishedISO,
    dateModified: publishedISO,
    author: { "@type": "Organization", name: apiPost.author?.name || "AXOIX" },
    publisher: {
      "@type": "Organization",
      name: "AXOIX",
      logo: { "@type": "ImageObject", url: "https://axoix.com/og-image.png" },
    },
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": `https://axoix.com/blog/${apiPost.slug}`,
    },
    commentCount: apiPost.comment_count || 0,
  };

  return (
    <Fragment>
      <SEO
        title={apiPost.meta_title || apiPost.title}
        description={metaDescription}
        path={`/blog/${apiPost.slug}`}
        type="article"
        schema={articleSchema}
      />
      <style>{articleStyles}</style>
        <div className="about-page inner-page">
          <div className="body_wrap o-clip">
            <Header />
            <main>
              <PageTitle pageTitle="Blog" pagesub="Blog" />
              <section className="blog_details_section pt-70">
                <div className="container">
                  <div className="row mt-none-30 g-0 align-items-start">
                    <div className="col-lg-8 mt-30">
                      <div className="blog_details_content">
                        <span className="blog-article-tag">#{tag}</span>
                        <h2 className="mb-20" style={{ lineHeight: 1.3 }}>{apiPost.title}</h2>
                        <div className="blog-article-meta">
                          <span><i className="far fa-calendar me-2"></i>{formatDate(apiPost.published_at || apiPost.created_at)}</span>
                          {apiPost.reading_time_mins && (
                            <span><i className="far fa-clock me-2"></i>{apiPost.reading_time_mins} min read</span>
                          )}
                          <span><i className="far fa-user me-2"></i>{apiPost.author.name}</span>
                        </div>

                        {imgUrl && (
                          <img src={imgUrl} alt={apiPost.featured_image_alt || apiPost.title} className="blog-featured-image" />
                        )}

                        <div
                          className="blog-article-content"
                          dangerouslySetInnerHTML={{ __html: apiPost.content_html || apiPost.content || "" }}
                        />

                        {/* Share row */}
                        <ShareButtons
                          url={`https://axoix.com/blog/${apiPost.slug}`}
                          title={apiPost.title}
                          text={apiPost.excerpt || apiPost.title}
                          hashtags={["AXOIX", ...(apiPost.tags || [])]}
                        />

                        {/* Content-upgrade lead magnet (falls back to generic newsletter) */}
                        <div className="mt-40">
                          {magnet ? (
                            <NewsletterSignup
                              source={`lead_magnet:${magnet.slug}`}
                              variant="card"
                              magnet={{
                                slug: magnet.slug,
                                heading: magnet.title,
                                description: magnet.description,
                                ctaLabel: magnet.ctaLabel,
                                pdfUrl: magnet.pdfUrl,
                              }}
                            />
                          ) : (
                            <NewsletterSignup source="blog_article" variant="card" />
                          )}
                        </div>

                        {/* CTA */}
                        <div className="mt-40 p-30 text-center" style={{
                          background: "rgba(0,255,151,0.05)",
                          border: "1px solid rgba(0,255,151,0.2)",
                          borderRadius: 16
                        }}>
                          <h4 className="mb-10">Ready to try AXOIX?</h4>
                          <p className="opacity-75 mb-20" style={{ fontSize: 14 }}>
                            Start free — no credit card required. All 22 modules included.
                          </p>
                          <Link
                            to="/signup"
                            className="thm-btn agency-btn"
                            onClick={() => track("cta_click", { cta_id: "blog_article_cta" })}
                          >
                            <span className="text">Get Started Free</span>
                          </Link>
                        </div>

                        {/* Comments */}
                        <BlogComments
                          slug={apiPost.slug}
                          allowComments={apiPost.allow_comments !== false}
                          commentCount={apiPost.comment_count || 0}
                        />

                        {/* Related posts */}
                        {apiPost.category?.id && (
                          <RelatedPosts categoryId={apiPost.category.id} excludeSlug={apiPost.slug} />
                        )}
                      </div>
                    </div>
                    <BlogSidebar />
                  </div>
                </div>
              </section>
            </main>
            <Footer />
            <Scrollbar />
          </div>
        </div>
      </Fragment>
  );
};

export default BlogArticlePage;
